/**
 * PricingTab - Pricing plans management
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { PricingSliderSettings } from './PricingSliderSettings';
import { LoyaltyLadderSettings } from './LoyaltyLadderSettings';

export function PricingTab({ content, setContent }) {
  const updatePricing = (field, value) => {
    setContent(prev => ({ ...prev, pricing: { ...prev.pricing, [field]: value } }));
  };

  const updatePlan = (index, field, value) => {
    setContent(prev => {
      const newPlans = [...(prev.pricing?.plans || [])];
      newPlans[index] = { ...newPlans[index], [field]: value };
      return { ...prev, pricing: { ...prev.pricing, plans: newPlans } };
    });
  };

  const updatePlanFeature = (planIndex, featureIndex, value) => {
    setContent(prev => {
      const newPlans = [...(prev.pricing?.plans || [])];
      const newFeatures = [...(newPlans[planIndex].features || [])];
      newFeatures[featureIndex] = value;
      newPlans[planIndex] = { ...newPlans[planIndex], features: newFeatures };
      return { ...prev, pricing: { ...prev.pricing, plans: newPlans } };
    });
  };

  const addPlanFeature = (planIndex) => {
    setContent(prev => {
      const newPlans = [...(prev.pricing?.plans || [])];
      newPlans[planIndex] = { ...newPlans[planIndex], features: [...(newPlans[planIndex].features || []), 'New feature'] };
      return { ...prev, pricing: { ...prev.pricing, plans: newPlans } };
    });
  };

  const removePlanFeature = (planIndex, featureIndex) => {
    setContent(prev => {
      const newPlans = [...(prev.pricing?.plans || [])];
      newPlans[planIndex] = { ...newPlans[planIndex], features: newPlans[planIndex].features.filter((_, i) => i !== featureIndex) };
      return { ...prev, pricing: { ...prev.pricing, plans: newPlans } };
    });
  };

  const movePlanFeature = (planIndex, fromIndex, toIndex) => {
    setContent(prev => {
      const newPlans = [...(prev.pricing?.plans || [])];
      const features = [...(newPlans[planIndex].features || [])];
      if (toIndex < 0 || toIndex >= features.length) return prev;
      const [moved] = features.splice(fromIndex, 1);
      features.splice(toIndex, 0, moved);
      newPlans[planIndex] = { ...newPlans[planIndex], features };
      return { ...prev, pricing: { ...prev.pricing, plans: newPlans } };
    });
  };

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle>Pricing Section</CardTitle>
        <CardDescription>Edit pricing plans, titles, and details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section titles */}
        {/* A/B test toggle between the original cards layout and the new
            "Sign Your Contract" slider experience. Default 'cards' so the
            live site doesn't change on deploy. */}
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <Label className="text-base font-semibold block mb-1">Pricing Section Style</Label>
          <p className="text-sm text-gray-600 mb-3">
            Flip between the classic 3-tier cards and the new "Sign Your Contract" slider for A/B testing.
            The slider preview lives at <code className="text-xs bg-white px-1 rounded">/pricing-slider-mockup</code>.
          </p>
          <div className="inline-flex rounded-full bg-white border border-gray-300 p-1" data-testid="pricing-mode-toggle">
            <button
              type="button"
              onClick={() => updatePricing('mode', 'cards')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                (content.pricing?.mode || 'cards') === 'cards'
                  ? 'bg-[#007a33] text-white shadow'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="pricing-mode-cards-btn"
            >
              Cards (current)
            </button>
            <button
              type="button"
              onClick={() => updatePricing('mode', 'slider')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                content.pricing?.mode === 'slider'
                  ? 'bg-amber-500 text-white shadow'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="pricing-mode-slider-btn"
            >
              Slider (new)
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Currently showing: <strong>{content.pricing?.mode === 'slider' ? 'Sign Your Contract slider' : 'Classic cards'}</strong>. Save changes to apply on the live landing page.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Badge Text</Label><Input value={content.pricing?.badge || ''} onChange={(e) => updatePricing('badge', e.target.value)} placeholder="e.g., PRICING" data-testid="pricing-badge-input" /></div>
          <div><Label>Section Title</Label><Input value={content.pricing?.title || ''} onChange={(e) => updatePricing('title', e.target.value)} /></div>
          <div><Label>Section Subtitle</Label><Input value={content.pricing?.subtitle || ''} onChange={(e) => updatePricing('subtitle', e.target.value)} /></div>
        </div>
        <div>
          <Label>Footer Text</Label>
          <Input value={content.pricing?.footerText || ''} onChange={(e) => updatePricing('footerText', e.target.value)} placeholder="e.g. CANCEL ANYTIME · No in-app purchases" />
        </div>

        {/* Currency Conversion Rates */}
        <div>
          <Label className="text-base font-semibold mb-2 block">Currency Conversion Rates</Label>
          <p className="text-sm text-gray-500 mb-3">Prices are entered in EUR. Set the conversion rates for GBP and USD.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>EUR to GBP Rate</Label>
              <Input
                type="number"
                step="0.01"
                value={content.pricing?.currency_rates?.gbp || ''}
                onChange={(e) => updatePricing('currency_rates', { ...content.pricing?.currency_rates, gbp: e.target.value })}
                placeholder="e.g. 0.84"
                data-testid="currency-rate-gbp-input"
              />
            </div>
            <div>
              <Label>EUR to USD Rate</Label>
              <Input
                type="number"
                step="0.01"
                value={content.pricing?.currency_rates?.usd || ''}
                onChange={(e) => updatePricing('currency_rates', { ...content.pricing?.currency_rates, usd: e.target.value })}
                placeholder="e.g. 1.09"
                data-testid="currency-rate-usd-input"
              />
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {content.pricing?.plans?.map((plan, planIndex) => (
            <div key={plan.id || planIndex} className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">{plan.name}</span>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Highlighted</Label>
                  <Switch checked={plan.highlighted} onCheckedChange={(checked) => updatePlan(planIndex, 'highlighted', checked)} />
                </div>
              </div>
              <div><Label>Plan Name</Label><Input value={plan.name} onChange={(e) => updatePlan(planIndex, 'name', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Price (fallback)</Label>
                  <Input value={plan.price} onChange={(e) => updatePlan(planIndex, 'price', e.target.value)} />
                </div>
                <div><Label>Period</Label><Input value={plan.period} onChange={(e) => updatePlan(planIndex, 'period', e.target.value)} /></div>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 -mt-1">
                💡 The live price shown to visitors (and charged by Stripe) is managed in the <strong>Pricing Config</strong> tab — edit <em>{plan.name}</em>'s "Base Amount" there. This Price field is only used as a fallback if central pricing is missing.
              </p>
              <div><Label>Sub-Price Text</Label><Input value={plan.subPrice || ''} onChange={(e) => updatePlan(planIndex, 'subPrice', e.target.value)} placeholder="e.g. £83.88 per year" /></div>
              <div><Label>Description</Label><Input value={plan.description} onChange={(e) => updatePlan(planIndex, 'description', e.target.value)} /></div>
              <div><Label>Badge Text</Label><Input value={plan.badge || ''} onChange={(e) => updatePlan(planIndex, 'badge', e.target.value)} placeholder="e.g. RECOMMENDED, LIMITED" /></div>
              <div><Label>CTA Button Text</Label><Input value={plan.ctaText} onChange={(e) => updatePlan(planIndex, 'ctaText', e.target.value)} /></div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Features</Label>
                  <Button variant="ghost" size="sm" onClick={() => addPlanFeature(planIndex)}><Plus className="w-3 h-3" /></Button>
                </div>
                <div className="space-y-2">
                  {plan.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-1.5" data-testid={`plan-${planIndex}-feature-${featureIndex}`}>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => movePlanFeature(planIndex, featureIndex, featureIndex - 1)} disabled={featureIndex === 0} data-testid={`feature-up-${planIndex}-${featureIndex}`}>
                          <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => movePlanFeature(planIndex, featureIndex, featureIndex + 1)} disabled={featureIndex === plan.features.length - 1} data-testid={`feature-down-${planIndex}-${featureIndex}`}>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <Input value={feature} onChange={(e) => updatePlanFeature(planIndex, featureIndex, e.target.value)} className="flex-1" />
                      <Button variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => removePlanFeature(planIndex, featureIndex)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>

    <PricingSliderSettings content={content} setContent={setContent} />
    <LoyaltyLadderSettings content={content} setContent={setContent} />
    </div>
  );
}
